import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Plus, Trash2, XCircle } from "lucide-react";
import { API, inr, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Timeline } from "./OrderSuccess";

const inputCls = "w-full bg-ink border border-[#262626] px-4 py-3 text-sm outline-none focus:border-gold transition-colors";

export default function Account() {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [addr, setAddr] = useState({ label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [showAddr, setShowAddr] = useState(false);

  const loadOrders = () => API.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { loadOrders(); API.get("/custom-orders/my").then((r) => setCustomOrders(r.data)).catch(() => {}); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  useEffect(() => { document.title = "My Account | Dressedin by Urbanz"; }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const r = await API.put("/auth/profile", { name, phone });
      setUser(r.data);
      toast.success("Profile updated");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      await API.post("/auth/addresses", addr);
      const me = await API.get("/auth/me");
      setUser(me.data);
      setShowAddr(false);
      setAddr({ label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
      toast.success("Address saved");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const removeAddress = async (id) => {
    await API.delete(`/auth/addresses/${id}`);
    setUser((u) => ({ ...u, addresses: u.addresses.filter((a) => a.id !== id) }));
  };

  const cancelOrder = async (id) => {
    try {
      await API.post(`/orders/${id}/cancel`);
      loadOrders();
      toast.success("Order cancelled");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const TABS = [["orders", "Orders"], ["custom", "Custom"], ["stamps", "Stamp Card"], ["profile", "Profile"], ["addresses", "Addresses"]];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="account-page">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Hey, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-sm text-muted-foreground mb-8">{user?.email}</p>
      <div className="flex gap-2 mb-8 border-b border-[#262626]">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`account-tab-${k}`}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold border-b-2 transition-colors ${tab === k ? "border-gold text-gold" : "border-transparent text-muted-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="space-y-5" data-testid="order-history">
          {orders.length === 0 && (
            <p className="text-muted-foreground text-sm">No orders yet. <Link to="/shop" className="text-gold hover:underline">Start here.</Link></p>
          )}
          {orders.map((o) => (
            <div key={o.id} className="border border-[#262626] bg-ink-surface p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{o.created_at.slice(0, 10)} · {o.payment.method.toUpperCase()} · {o.payment.status}</p>
                </div>
                <p className="text-gold font-display font-bold">{inr(o.total)}</p>
              </div>
              <div className="mt-4"><Timeline status={o.status} timeline={o.timeline} /></div>
              <div className="flex flex-wrap gap-2 mt-4">
                {o.items.map((i) => <img key={i.sku + i.size} src={i.image} alt={i.name} title={i.name} className="h-14 w-11 object-cover border border-[#262626]" loading="lazy" />)}
              </div>
              <div className="flex gap-3 mt-4">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/invoice`}
                  data-testid={`invoice-${o.order_number}`}
                  className="inline-flex items-center gap-1.5 text-xs text-gold border border-gold/50 px-3 py-2 hover:bg-gold/10 transition-colors">
                  <Download size={12} /> Invoice
                </a>
                {["placed", "confirmed"].includes(o.status) && (
                  <button onClick={() => cancelOrder(o.id)} data-testid={`cancel-${o.order_number}`}
                    className="inline-flex items-center gap-1.5 text-xs text-red-400 border border-red-400/40 px-3 py-2 hover:bg-red-400/10 transition-colors">
                    <XCircle size={12} /> Cancel order
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "custom" && (
        <div className="space-y-4" data-testid="custom-orders-list">
          {customOrders.length === 0 && (
            <p className="text-muted-foreground text-sm">No custom orders yet. <Link to="/customize" className="text-gold hover:underline">Design your first piece.</Link></p>
          )}
          {customOrders.map((o) => (
            <div key={o.id} className="border border-[#262626] bg-ink-surface p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`custom-order-${o.ref}`}>
              <div>
                <p className="font-display font-bold">{o.ref} <span className="text-muted-foreground font-normal text-xs">· {o.created_at.slice(0, 10)}</span></p>
                <p className="text-xs text-muted-foreground mt-1">
                  {o.garment_label} — {o.size}/{o.color} × {o.qty}{o.text ? ` · "${o.text}" (${o.text_color}, ${o.placement})` : ""}{o.design_name ? ` · Design: ${o.design_name}` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="text-gold font-bold">{inr(o.total)}</p>
                <span className={`text-[10px] uppercase tracking-widest font-bold ${o.status === "delivered" ? "text-green-400" : o.status === "cancelled" ? "text-red-400" : "text-gold"}`}>
                  {o.status.replace(/_/g, " ")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "stamps" && (
        <div className="max-w-md border border-gold/40 bg-ink-surface p-8" data-testid="stamp-card-tab">
          <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-2">DN Loyalty</p>
          <h2 className="font-display font-bold text-2xl">Your stamp card</h2>
          <div className="flex gap-2 my-6">
            {Array(5).fill(0).map((_, i) => (
              <div key={`slot-${i}`} data-testid={`stamp-slot-${i}`}
                className={`h-14 flex-1 border flex items-center justify-center transition-colors ${i < (user?.stamps || 0) ? "bg-gold border-gold" : "border-[#262626]"}`}>
                {i < (user?.stamps || 0) && <img src="/logo.png" alt="stamp" className="h-9 w-9 object-contain" />}
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            {(user?.stamps || 0) >= 5
              ? "You've got 5 stamps! Tick the stamp-card box at cart or checkout for 40% off."
              : `${5 - (user?.stamps || 0)} more order${5 - (user?.stamps || 0) === 1 ? "" : "s"} to unlock 40% off. Earn 1 stamp per order.`}
          </p>
        </div>
      )}

      {tab === "profile" && (
        <form onSubmit={saveProfile} className="max-w-md space-y-4" data-testid="profile-form">
          <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Full name" className={inputCls} />
          <input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className={inputCls} />
          <button data-testid="profile-save-btn" className="btn-gold px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em]">Save profile</button>
        </form>
      )}

      {tab === "addresses" && (
        <div data-testid="addresses-section">
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {(user?.addresses || []).map((a) => (
              <div key={a.id} className="border border-[#262626] bg-ink-surface p-5 relative" data-testid={`address-${a.id}`}>
                <p className="text-xs uppercase tracking-widest text-gold font-bold">{a.label}</p>
                <p className="text-sm font-bold mt-2">{a.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />{a.city}, {a.state} — {a.pincode}<br />{a.phone}</p>
                <button onClick={() => removeAddress(a.id)} data-testid={`delete-address-${a.id}`}
                  className="absolute top-4 right-4 text-muted-foreground hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          {showAddr ? (
            <form onSubmit={addAddress} className="border border-[#262626] bg-ink-surface p-6 grid sm:grid-cols-2 gap-4 max-w-2xl" data-testid="address-form">
              <input data-testid="addr-label" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} placeholder="Label (Home/Work)" className={inputCls} />
              <input data-testid="addr-fullname" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} required placeholder="Full name" className={inputCls} />
              <input data-testid="addr-ph" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} required placeholder="Phone" className={inputCls} />
              <input data-testid="addr-pin" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} required placeholder="Pincode" className={inputCls} />
              <input data-testid="addr-l1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} required placeholder="Address line 1" className={`${inputCls} sm:col-span-2`} />
              <input data-testid="addr-l2" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} placeholder="Address line 2" className={`${inputCls} sm:col-span-2`} />
              <input data-testid="addr-cty" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} required placeholder="City" className={inputCls} />
              <input data-testid="addr-st" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} required placeholder="State" className={inputCls} />
              <div className="sm:col-span-2 flex gap-3">
                <button data-testid="address-save-btn" className="btn-gold px-8 py-3 text-xs font-bold uppercase tracking-[0.2em]">Save address</button>
                <button type="button" onClick={() => setShowAddr(false)} className="text-muted-foreground text-xs uppercase tracking-widest">Cancel</button>
              </div>
            </form>
          ) : (
            <button onClick={() => setShowAddr(true)} data-testid="add-address-btn"
              className="inline-flex items-center gap-2 border border-gold text-gold px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-gold/10 transition-colors">
              <Plus size={14} /> Add address
            </button>
          )}
        </div>
      )}
    </div>
  );
}
